import { userProfileService } from '@/services/user-profile.service';

const initialState = () => ({
  searchFilters: {},
  status: {
    error: null,
    pending: true,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const actions = {
  getSearchFilters({ commit }) {
    commit('GET_SEARCH_FILTERS');

    return userProfileService.getSearchFilters().then(
      (searchFilters) => commit('GET_SEARCH_FILTERS_SUCCESS', searchFilters),
      (error) => commit('ERROR', error),
    );
  },
  setSearchFilters({ commit, state }) {
    commit('SET_SEARCH_FILTERS');

    return userProfileService.setSearchFilters(state.searchFilters).then(
      (searchFilters) => commit('SET_SEARCH_FILTERS_SUCCESS', searchFilters),
      (error) => commit('ERROR', error),
    );
  },
  setPlatforms({ commit }, platforms) {
    commit('SET_PLATFORMS', platforms);
  },
  setAdministrativeRegions({ commit }, { platform, regions }) {
    commit('SET_ADMINISTRATIVE_REGIONS', { platform, regions });
  },
  setClassifications({ commit }, { classifications }) {
    commit('SET_CLASSIFICATIONS', { classifications });
  },
  setStatuses({ commit }, statuses) {
    commit('SET_STATUSES', statuses);
  },
  setClosingBefore({ commit }, closingBefore) {
    commit('SET_CLOSING_BEFORE', closingBefore);
  },
  setPublicationDates({ commit }, publicationDates) {
    commit('SET_PUBLICATION_DATES', publicationDates);
  },
  setArchived({ commit }, archived) {
    commit('SET_ARCHIVED', archived);
  },
  setAllKeywords({ commit }, allKeywords) {
    commit('SET_ALL_KEYWORDS', allKeywords);
  },
  setReadFilters({ commit }, readFilters) {
    commit('SET_READ_FILTERS', readFilters);
  },
  setFollowFilters({ commit }, followFilters) {
    commit('SET_FOLLOW_FILTERS', followFilters);
  },
  setShareFilters({ commit }, shareFilters) {
    commit('SET_SHARE_FILTERS', shareFilters);
  },
  setAssigneesFilters({ commit }, assigneesFilters) {
    commit('SET_ASSIGNEES_FILTERS', assigneesFilters);
  },
  setRelevanceScoreFilters({ commit }, relevanceScoreFilters) {
    commit('SET_RELEVANCE_SCORE_FILTERS', relevanceScoreFilters);
  },
  setQueryFilters({ commit }, query) {
    commit('SET_QUERY_FILTERS', query);
  },
  setAdvancedSearchFilters({ commit }, { keywordIds, matchContexts, procurementCategories, zones, includeUnlocated }) {
    commit('SAVE_ADVANCED_SEARCH_FILTERS', {
      keywordIds,
      matchContexts,
      procurementCategories,
      zones,
      includeUnlocated,
    });
  },
  // Replace the whole working filter set at once (used when switching tender views). Callers
  // pass a complete set from viewFiltersToWorking, so every filter is taken from the target
  // view — including query/publication dates, which reset on switch (they are not carried over).
  loadAllFilters({ commit }, filters) {
    commit('LOAD_ALL_FILTERS', filters);
  },
};

const mutations = {
  GET_SEARCH_FILTERS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
    state.searchFilters = {};
  },
  GET_SEARCH_FILTERS_SUCCESS(state, searchFilters) {
    state.status = {
      error: null,
      pending: false,
      resolved: false,
      rejected: false,
    };
    state.searchFilters = searchFilters;
  },
  SET_SEARCH_FILTERS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  SET_SEARCH_FILTERS_SUCCESS(state, searchFilters) {
    state.status = {
      error: null,
      pending: false,
      resolved: false,
      rejected: false,
    };
    state.searchFilters = {
      ...searchFilters,
      publishedAfter: state.searchFilters.publishedAfter,
      publishedBefore: state.searchFilters.publishedBefore,
      relevanceScoreRange: state.searchFilters.relevanceScoreRange,
    };
  },
  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  SET_PLATFORMS(state, platforms) {
    state.searchFilters.platforms = platforms;
  },
  SET_ADMINISTRATIVE_REGIONS(state, { platform, regions }) {
    // Accept regions as an array and convert to object format for storage
    if (!state.searchFilters.administrativeRegions || typeof state.searchFilters.administrativeRegions !== 'object') {
      state.searchFilters.administrativeRegions = {};
    }
    // If regions is an array, store as { platform: regions }
    if (Array.isArray(regions)) {
      state.searchFilters.administrativeRegions[platform] = regions;
    } else {
      // If regions is already an object, merge
      Object.assign(state.searchFilters.administrativeRegions, regions);
    }
  },
  SET_CLASSIFICATIONS(state, { classifications }) {
    state.searchFilters.classifications = classifications;
  },
  SET_STATUSES(state, statuses) {
    state.searchFilters.statuses = statuses;
  },
  SET_CLOSING_BEFORE(state, closingBefore) {
    state.searchFilters.closingBefore = closingBefore;
  },
  SET_PUBLICATION_DATES(state, publicationDates) {
    state.searchFilters.publishedAfter = publicationDates.publishedAfter;
    state.searchFilters.publishedBefore = publicationDates.publishedBefore;
  },
  SET_ARCHIVED(state, archived) {
    state.searchFilters = { ...state.searchFilters, archived };
  },
  SET_ALL_KEYWORDS(state, allKeywords) {
    state.searchFilters = { ...state.searchFilters, allKeywords };
  },
  SET_READ_FILTERS(state, readFilters) {
    state.searchFilters.readFilters = readFilters;
  },
  SET_FOLLOW_FILTERS(state, followFilters) {
    state.searchFilters.followFilters = followFilters;
  },
  SET_SHARE_FILTERS(state, shareFilters) {
    state.searchFilters.shareFilters = shareFilters;
  },
  SET_ASSIGNEES_FILTERS(state, assigneesFilters) {
    state.searchFilters.assigneesFilters = assigneesFilters;
  },
  SET_RELEVANCE_SCORE_FILTERS(state, relevanceScoreFilters) {
    state.searchFilters.relevanceScoreRange = relevanceScoreFilters;
  },
  SET_QUERY_FILTERS(state, query) {
    state.searchFilters.query = query;
  },
  SAVE_ADVANCED_SEARCH_FILTERS(state, { keywordIds, matchContexts, procurementCategories, zones, includeUnlocated }) {
    state.searchFilters.keywordIds = keywordIds;
    state.searchFilters.matchContexts = matchContexts;
    state.searchFilters.procurementCategories = procurementCategories;
    state.searchFilters.zones = zones;
    if (includeUnlocated !== undefined) {
      // Replace the object so the key stays reactive even when it did not exist yet.
      state.searchFilters = { ...state.searchFilters, includeUnlocated };
    }
  },
  LOAD_ALL_FILTERS(state, filters) {
    // Callers pass a complete working set (viewFiltersToWorking fills every key), so replace
    // the working filters outright — no stale keys leak across a view switch.
    state.searchFilters = { ...filters };
  },
  RESET(state) {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const getters = {
  searchFilters: (state) => state.searchFilters || {},

  // filters
  assigneesFilters: (state) => state.searchFilters.assigneesFilters || [],
  closingBefore: (state) => state.searchFilters.closingBefore || null,
  followFilters: (state) => state.searchFilters.followFilters || [],
  matchContexts: (state) => state.searchFilters.matchContexts || [],
  keywordIds: (state) => state.searchFilters.keywordIds || [],
  platforms: (state) => state.searchFilters.platforms || [],
  administrativeRegions: (state) => state.searchFilters.administrativeRegions || {},
  procurementCategories: (state) => state.searchFilters.procurementCategories || [],
  readFilters: (state) => state.searchFilters.readFilters || [],
  shareFilters: (state) => state.searchFilters.shareFilters || [],
  statuses: (state) => state.searchFilters.statuses || [],
  zones: (state) => state.searchFilters.zones || {},
  classificationFilters: (state) => state.searchFilters.classifications || [],

  // Ephemeral filters
  publishedAfter: (state) => state.searchFilters.publishedAfter || null,
  publishedBefore: (state) => state.searchFilters.publishedBefore || null,
  relevanceScoreFilters: (state) => state.searchFilters.relevanceScoreFilters || [],

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const searchFilters = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
